import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { usePipeline } from '../hooks/usePipeline';
import { PipelineCard, PipelineFilters } from '../types/pipeline';
import CommonPageHeader from '../components/common/CommonPageHeader';
import PipelineMetrics from '../components/pipeline/PipelineMetrics';
import PipelineStage from '../components/pipeline/PipelineStage';
import PipelineFiltersPanel from '../components/pipeline/PipelineFiltersPanel';
import PipelineCardModal from '../components/pipeline/PipelineCardModal';
import QuotationFormSimple from '../components/quotations/QuotationFormSimple';
import LoadingScreen from '../components/common/LoadingScreen';
import { 
  FunnelIcon,
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const PipelineView: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasAccess } = usePermissions();
  const {
    pipelineData,
    metrics,
    loading,
    loadPipelineData,
    moveCard,
    convertDocument
  } = usePipeline();

  const [filters, setFilters] = useState<PipelineFilters>({
    organizationId: user?.organizationId
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCard, setSelectedCard] = useState<PipelineCard | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [editingCard, setEditingCard] = useState<PipelineCard | null>(null);

  // Pipeline adatok betöltése
  useEffect(() => {
    if (user?.organizationId) {
      loadPipelineData(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organizationId]);

  // Jogosultság ellenőrzés
  if (!hasAccess('quotations')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.errors.noPermission')}</div>
      </div>
    );
  }

  // Organization ellenőrzés
  if (!user?.organizationId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Nincs szervezet hozzárendelve</div>
      </div>
    );
  }

  // Frissítés
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadPipelineData(filters);
      toast.success('Pipeline frissítve');
    } catch (error) {
      console.error('Error refreshing pipeline:', error);
      toast.error('Hiba a frissítés során');
    } finally {
      setRefreshing(false);
    }
  };

  // Kártya mozgatása
  const handleCardMove = async (cardId: string, fromStage: string, toStage: string) => {
    try {
      await moveCard(cardId, fromStage as any, toStage as any);
      toast.success('Dokumentum áthelyezve');
    } catch (error) {
      console.error('Error moving card:', error);
      toast.error('Hiba az áthelyezés során');
    }
  };

  // Kártya kattintás
  const handleCardClick = (card: PipelineCard) => {
    setSelectedCard(card);
  };

  // Státusz váltás
  const handleStatusChange = async (cardId: string, newStatus: string) => {
    try {
      // TODO: Implementáld a státusz frissítést a backend-en
      console.log('Status change:', cardId, newStatus);
      toast.success(`Státusz frissítve: ${newStatus}`);
      await loadPipelineData(filters);
      setSelectedCard(null);
    } catch (error) {
      console.error('Error changing status:', error);
      toast.error('Hiba a státusz váltás során');
    }
  };

  // Szerkesztés
  const handleEdit = (card: PipelineCard) => {
    setEditingCard(card);
    setSelectedCard(null);
  };

  // Szerkesztés mentése
  const handleEditSubmit = async (data: any) => {
    try {
      // TODO: Implementáld a dokumentum frissítést
      console.log('Update document:', data);
      toast.success('Dokumentum frissítve');
      setEditingCard(null);
      await loadPipelineData(filters);
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error('Hiba a mentés során');
    }
  };

  if (loading && !pipelineData.length) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <CommonPageHeader
        title="Pipeline Nézet"
        subtitle="Értékesítési folyamat áttekintése"
        icon={FunnelIcon}
      >
        <div className="flex gap-2">
          {/* Szűrők gomb */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors ${
              showFilters ? 'bg-white/30' : ''
            }`}
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
            <span>Szűrők</span>
          </button>

          {/* Frissítés gomb */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Frissítés</span>
          </button>

          {/* Metrikák gomb */}
          <button
            className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <ChartBarIcon className="h-4 w-4 mr-2" />
            <span>Metrikák</span>
          </button>
        </div>
      </CommonPageHeader>

      <div className="w-full px-6 py-6">
        {/* Szűrők panel */}
        {showFilters && (
          <div className="mb-6">
            <PipelineFiltersPanel
              filters={filters}
              onChange={setFilters}
              onClose={() => setShowFilters(false)}
            />
          </div>
        )}

        {/* Metrikák */}
        {metrics && (
          <div className="mb-6">
            <PipelineMetrics metrics={metrics} />
          </div>
        )}

        {/* Kanban Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {pipelineData.map((stage) => (
            <PipelineStage
              key={stage.stage}
              stage={stage}
              onCardClick={handleCardClick}
              onCardMove={handleCardMove}
            />
          ))}
        </div>

        {/* Üres állapot */}
        {pipelineData.every((stage) => stage.cards.length === 0) && (
          <div className="text-center py-12">
            <FunnelIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nincs adat</h3>
            <p className="mt-1 text-sm text-gray-500">
              Kezdj el dolgozni ajánlatokkal, megrendelésekkel vagy számlákkal.
            </p>
          </div>
        )}
      </div>

      {/* Kártya részletek modal */}
      {selectedCard && (
        <PipelineCardModal
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
          onMove={handleCardMove}
          onConvert={convertDocument}
          onEdit={handleEdit}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* Szerkesztő modal */}
      {editingCard && editingCard.documentType === 'quotation' && (
        <QuotationFormSimple
          isOpen={true}
          onCancel={() => setEditingCard(null)}
          onSubmit={handleEditSubmit}
          initialData={editingCard as any}
        />
      )}

      {/* TODO: Add more editors for other document types */}
      {editingCard && editingCard.documentType !== 'quotation' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md">
            <h3 className="text-lg font-semibold mb-4">Szerkesztő nem elérhető</h3>
            <p className="text-gray-600 mb-4">
              A(z) "{editingCard.documentType}" típusú dokumentumok szerkesztője még nem implementált.
            </p>
            <button
              onClick={() => setEditingCard(null)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Bezárás
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineView;
